import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@aix/ui"
import dayjs from "dayjs"
import { CheckCircle2, Clipboard, KeyRound, Loader2, ShieldAlert, Trash2 } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"

import { trpc } from "../lib/trpc"
import type { EAppTokenScope } from "../../../aixinternal-api/src/constants/appTokenConfig"

const DEFAULT_TTL_DAYS = 365
const DEFAULT_LABEL = "AIX Internal observability automation"

const SCOPE_LABELS: Record<EAppTokenScope, string> = {
  "ai-gateway:vidmax:dev-infer": "VidMax DEV AI inference",
  "deploy-feed:read": "Deploy Feed",
  "deploy-feed:write": "Deploy Feed reconciliation",
  "uptime:read": "Uptime monitors",
  "api-monitors:read": "API monitors",
  "api-monitors:sync": "API monitor synchronization",
  "incidents:read": "Incidents",
  "vendor-status:read": "Vendor status",
  "seo:automation": "SEO automation",
  "seo:publish": "SEO publishing",
  "reporting:read": "Portfolio reporting (read only)",
  "reporting:ingest": "Portfolio data ingestion and snapshots",
  "engineering-delivery:read": "Engineering delivery (read only)",
  "engineering-delivery:ingest": "Engineering delivery ingestion",
  "billing-analytics:read": "Billing analytics",
  "billing-analytics:ingest": "Billing analytics ingestion",
  "marketing-analytics:read": "Marketing analytics",
  "marketing-analytics:ingest": "Marketing analytics ingestion",
}

export function AccessTokensPanel() {
  const utils = trpc.useUtils()
  const [companyId, setCompanyId] = useState("")
  const [label, setLabel] = useState(DEFAULT_LABEL)
  const [ttlDays, setTtlDays] = useState(DEFAULT_TTL_DAYS)
  const [selectedScopes, setSelectedScopes] = useState<EAppTokenScope[]>([])
  const [createdToken, setCreatedToken] = useState<string | null>(null)

  const companiesQuery = trpc.companies.list.useQuery({ status: "active" })
  const presetsQuery = trpc.tokens.scopePresets.useQuery()
  const tokensQuery = trpc.tokens.list.useQuery()

  const observabilityScopes = useMemo(() => {
    return presetsQuery.data?.observabilityRead ?? []
  }, [presetsQuery.data?.observabilityRead])

  useEffect(() => {
    if (companyId === "" && companiesQuery.data?.companies[0]?.id) {
      setCompanyId(companiesQuery.data.companies[0].id)
    }
  }, [companiesQuery.data?.companies, companyId])

  useEffect(() => {
    if (selectedScopes.length === 0 && observabilityScopes.length > 0) {
      setSelectedScopes([...observabilityScopes])
    }
  }, [observabilityScopes, selectedScopes.length])

  const mintMutation = trpc.tokens.mintService.useMutation({
    onSuccess: (result) => {
      setCreatedToken(result.token)
      toast.success("Service token created")
      void utils.tokens.list.invalidate()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const revokeMutation = trpc.tokens.revoke.useMutation({
    onSuccess: () => {
      toast.success("Token revoked")
      void utils.tokens.list.invalidate()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const toggleScope = (scope: EAppTokenScope, checked: boolean) => {
    setSelectedScopes((current) => {
      return checked ? [...new Set([...current, scope])] : current.filter((currentScope) => currentScope !== scope)
    })
  }

  const handleMint = () => {
    if (companyId === "" || label.trim() === "" || selectedScopes.length === 0) {
      toast.error("Choose a company, label, and at least one scope")
      return
    }

    mintMutation.mutate({
      app: "aixinternal-api",
      companyId,
      label: label.trim(),
      ttlDays,
      scopes: selectedScopes,
    })
  }

  const copyCreatedToken = async () => {
    if (createdToken === null) return
    try {
      await navigator.clipboard.writeText(createdToken)
      toast.success("Token copied")
    } catch {
      toast.error("Could not copy token")
    }
  }

  const isLoading = companiesQuery.isLoading || presetsQuery.isLoading || tokensQuery.isLoading

  return (
    <div className="space-y-8 p-6">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <KeyRound className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-heading text-2xl font-semibold">Access tokens</h2>
          <p className="text-sm text-muted-foreground">Create revocable, read-only credentials for automations.</p>
        </div>
      </div>

      <section className="space-y-5 border border-border bg-background p-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Observability read-only</p>
          <h3 className="mt-1 font-heading text-xl font-semibold">Create a 365-day service token</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Requires a super-admin account with MFA. Scopes cannot be changed after creation.
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading token configuration
          </div>
        ) : (
          <div className="grid gap-5 lg:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="service-token-company">
                Company
              </label>
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger id="service-token-company">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companiesQuery.data?.companies.map((company) => {
                    return (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="service-token-days">
                Lifetime in days
              </label>
              <Input
                id="service-token-days"
                type="number"
                min={1}
                max={365}
                value={ttlDays}
                onChange={(event) => {
                  setTtlDays(Math.min(365, Math.max(1, Number(event.target.value))))
                }}
              />
            </div>

            <div className="space-y-2 lg:col-span-2">
              <label className="text-sm font-medium" htmlFor="service-token-label">
                Label
              </label>
              <Input
                id="service-token-label"
                value={label}
                onChange={(event) => {
                  setLabel(event.target.value)
                }}
              />
            </div>

            <fieldset className="space-y-3 lg:col-span-2">
              <legend className="text-sm font-medium">Read scopes</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                {observabilityScopes.map((scope) => {
                  return (
                    <label
                      key={scope}
                      className="flex cursor-pointer items-center gap-3 border border-border bg-card p-3 text-sm"
                    >
                      <Checkbox
                        checked={selectedScopes.includes(scope)}
                        onCheckedChange={(checked) => {
                          toggleScope(scope, checked === true)
                        }}
                      />
                      <span>
                        <span className="block font-medium">{SCOPE_LABELS[scope] ?? scope}</span>
                        <span className="font-mono text-xs text-muted-foreground">{scope}</span>
                      </span>
                    </label>
                  )
                })}
              </div>
            </fieldset>
          </div>
        )}

        <Button type="button" onClick={handleMint} loading={mintMutation.isLoading} disabled={isLoading}>
          <KeyRound className="mr-2 h-4 w-4" /> Create service token
        </Button>
      </section>

      {createdToken !== null && (
        <section className="space-y-4 border-2 border-primary bg-primary/5 p-5">
          <div className="flex items-center gap-2 font-semibold">
            <ShieldAlert className="h-5 w-5 text-primary" /> Copy this token now. It will not be shown again.
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input value={createdToken} readOnly className="font-mono text-xs" aria-label="New service token" />
            <Button
              type="button"
              onClick={() => {
                void copyCreatedToken()
              }}
            >
              <Clipboard className="mr-2 h-4 w-4" /> Copy
            </Button>
          </div>
          <ol className="grid gap-2 text-sm sm:grid-cols-2">
            <li>
              <strong>1.</strong> Store as <code>/automation/AIXINTERNAL_OBSERVABILITY_TOKEN</code> in Infisical.
            </li>
            <li>
              <strong>2.</strong> Point cron and monitor skills at that secret.
            </li>
            <li>
              <strong>3.</strong> Verify Deploy Feed and uptime read calls.
            </li>
            <li>
              <strong>4.</strong> Revoke the previous credential after verification.
            </li>
          </ol>
        </section>
      )}

      <section className="space-y-4">
        <div>
          <h3 className="font-heading text-xl font-semibold">Issued tokens</h3>
          <p className="text-sm text-muted-foreground">
            Only prefixes and metadata are retained. Raw values cannot be recovered.
          </p>
        </div>
        <div className="space-y-3">
          {tokensQuery.data?.length === 0 && (
            <p className="border border-dashed border-border p-4 text-sm text-muted-foreground">No tokens issued.</p>
          )}
          {tokensQuery.data?.map((token) => {
            const displayName = token.label || token.tokenPrefix
            return (
              <article
                key={token.id}
                className="flex flex-col gap-4 border border-border bg-background p-4 lg:flex-row lg:items-center lg:justify-between"
              >
                <div className="min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <strong>{token.label || "Unlabeled token"}</strong>
                    <Badge variant={token.status === "active" ? "default" : "secondary"}>{token.status}</Badge>
                    <Badge variant="outline">{token.tokenType}</Badge>
                  </div>
                  <p className="font-mono text-xs text-muted-foreground">{token.tokenPrefix}...</p>
                  <div className="flex flex-wrap gap-1">
                    {token.scopes.map((scope) => {
                      return (
                        <Badge key={scope} variant="outline">
                          {scope}
                        </Badge>
                      )
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Expires {dayjs(token.expiresAt).format("MMM D, YYYY h:mm A")}
                  </p>
                </div>
                {token.status === "active" && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button type="button" variant="destructive" size="sm" loading={revokeMutation.isLoading}>
                        <Trash2 className="mr-2 h-4 w-4" /> Revoke
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Revoke {displayName}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This immediately disables the credential and cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => {
                            revokeMutation.mutate({ id: token.id })
                          }}
                        >
                          Revoke token
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </article>
            )
          })}
        </div>
      </section>

      <div className="flex items-start gap-2 border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        Add future access by creating a replacement token with the expanded scopes, update Infisical, verify it, then
        revoke the old token.
      </div>
    </div>
  )
}
